import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Save, Megaphone } from "lucide-react";
import { BriefingRadar } from "./BriefingRadar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtChf = (v) => (v > 0 ? `CHF ${v.toFixed(2)}` : "–");

export const AdsTab = ({ adminKey }) => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [budgets, setBudgets] = useState({});
  const [newCampaign, setNewCampaign] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/ads/report?days=${days}`, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => {
        setReport(r.data);
        const b = {};
        (r.data.campaigns || []).forEach((c) => { b[c.campaign] = c.budget || 0; });
        setBudgets(b);
      })
      .catch(() => {});
  }, [adminKey, days]);

  useEffect(load, [load]);

  const saveBudgets = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ads/budgets`, { budgets }, { headers: { "X-Admin-Key": adminKey } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      load();
    } catch { /* noop */ }
    setSaving(false);
  };

  const addCampaign = () => {
    const c = newCampaign.trim();
    if (!c || budgets[c] !== undefined) return;
    setBudgets((b) => ({ ...b, [c]: 0 }));
    setNewCampaign("");
  };

  if (!report) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;

  const rows = (report.campaigns || []).map((c) => ({ ...c, budget: budgets[c.campaign] ?? c.budget ?? 0 }));
  Object.keys(budgets).forEach((c) => {
    if (!rows.find((r) => r.campaign === c)) rows.push({ campaign: c, sources: [], visits: 0, leads: 0, budget: budgets[c] });
  });
  const totals = rows.reduce((a, r) => ({
    visits: a.visits + r.visits, leads: a.leads + r.leads, budget: a.budget + (Number(r.budget) || 0),
  }), { visits: 0, leads: 0, budget: 0 });

  return (
    <div className="mt-8" data-testid="ads-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone size={18} style={{ color: "var(--camel)" }} />
          <h2 className="font-serif-display text-xl font-semibold">Werbe-Auswertung</h2>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`ads-days-${d}`} onClick={() => setDays(d)}
              className="rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors"
              style={{ borderColor: days === d ? "var(--camel)" : "var(--line)", background: days === d ? "var(--camel)" : "transparent", color: days === d ? "#fff" : "var(--on-surface)" }}>
              {d} Tage
            </button>
          ))}
        </div>
      </div>
      <p className="mt-2 text-xs" style={{ color: "var(--muted-warm)" }}>
        Besucher über UTM-Links (utm_campaign), Leads = Boutique-Anfragen von Kampagnen-Besuchern. Budget = dein Monatsbudget pro Kampagne in CHF; daraus berechnen sich Kosten pro Besucher und pro Lead.
      </p>

      <div className="mt-5 grid grid-cols-3 gap-3">
        {[["Besucher", totals.visits, "ads-total-visits"], ["Leads", totals.leads, "ads-total-leads"], ["Budget/Monat", totals.budget > 0 ? `CHF ${totals.budget.toFixed(0)}` : "–", "ads-total-budget"]].map(([label, val, tid]) => (
          <div key={tid} className="rounded-2xl border p-4 text-center" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
            <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>{label}</p>
            <p className="mt-1 font-serif-display text-2xl font-semibold" data-testid={tid}>{val}</p>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="mt-6 rounded-2xl border p-8 text-center text-sm" style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }} data-testid="ads-empty">
          Noch keine Kampagnen-Besuche getrackt. Sobald jemand über einen UTM-Link (z.B. ?utm_source=meta&utm_campaign=b2c_schrank) kommt, erscheint die Kampagne hier automatisch. Du kannst Kampagnen auch unten manuell anlegen, um Budgets vorzubereiten.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }}>
          <table className="w-full text-left" data-testid="ads-report-table">
            <thead style={{ background: "var(--surface-secondary)" }}>
              <tr>
                {["Kampagne", "Quelle", "Besucher", "Leads", "Budget CHF/Mt", "CHF/Besucher", "CHF/Lead"].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const b = Number(r.budget) || 0;
                return (
                  <tr key={r.campaign} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`ads-row-${r.campaign}`}>
                    <td className="px-4 py-3 text-sm font-semibold">{r.campaign}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{(r.sources || []).join(", ") || "–"}</td>
                    <td className="px-4 py-3 text-sm" data-testid={`ads-visits-${r.campaign}`}>{r.visits}</td>
                    <td className="px-4 py-3 text-sm" data-testid={`ads-leads-${r.campaign}`}>{r.leads}</td>
                    <td className="px-4 py-3">
                      <input type="number" min="0" step="10" value={budgets[r.campaign] ?? 0}
                        data-testid={`ads-budget-input-${r.campaign}`}
                        onChange={(e) => setBudgets((bs) => ({ ...bs, [r.campaign]: e.target.value === "" ? 0 : Number(e.target.value) }))}
                        className="w-24 rounded-lg border px-2 py-1.5 text-sm"
                        style={{ borderColor: "var(--line)", background: "var(--surface)" }} />
                    </td>
                    <td className="px-4 py-3 text-sm">{b > 0 && r.visits > 0 ? fmtChf(b / r.visits) : "–"}</td>
                    <td className="px-4 py-3 text-sm font-semibold" style={{ color: "var(--camel)" }}>{b > 0 && r.leads > 0 ? fmtChf(b / r.leads) : "–"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <input value={newCampaign} onChange={(e) => setNewCampaign(e.target.value)}
          placeholder="Kampagne manuell anlegen (z.B. b2c_schrank)"
          data-testid="ads-new-campaign-input"
          className="w-72 rounded-full border px-4 py-2 text-sm"
          style={{ borderColor: "var(--line)", background: "var(--surface)" }}
          onKeyDown={(e) => e.key === "Enter" && addCampaign()} />
        <button onClick={addCampaign} data-testid="ads-add-campaign-btn"
          className="rounded-full border px-4 py-2 text-xs font-medium transition-colors hover:border-[var(--camel)]"
          style={{ borderColor: "var(--line)" }}>
          + Hinzufügen
        </button>
        <button onClick={saveBudgets} disabled={saving} data-testid="ads-save-budgets-btn"
          className="flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold text-white transition-opacity disabled:opacity-60"
          style={{ background: "var(--camel)" }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {saved ? "Gespeichert ✓" : "Budgets speichern"}
        </button>
      </div>

      <BriefingRadar adminKey={adminKey} />
    </div>
  );
};
